/* eslint-disable @typescript-eslint/no-misused-promises */
/**
 * Login Form
 */

import {
  Button,
  Heading,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
  VStack,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import TextField from "components/base/atoms/TextField";
import useAuth from "hooks/useAuth";
import { useForm } from "react-hook-form";
import { setFormErrors } from "utils";
import { LoginSchema } from "config/schema/LoginSchema";
import { ModalTypes } from "config/types/modals";
import { useLoginQuery } from "_actions/AuthActions";
import { SecondaryButton } from "../base/atoms/HJSButton";

interface ILoginValues {
  email: string;
  password: string;
}
interface IModalComponentProps {
  changeModalType: (modalType: ModalTypes) => void;
  onClose: () => void;
}
type TModalComponent = (props: IModalComponentProps) => JSX.Element;

const LoginForm: TModalComponent = ({
  changeModalType,
  onClose,
}: IModalComponentProps) => {
  const { setAuthToken } = useAuth();

  const initialFormValues: ILoginValues = {
    email: "",
    password: "",
  };
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<ILoginValues>({
    resolver: yupResolver(LoginSchema),
    defaultValues: initialFormValues,
  });

  const { mutate: loginUser, isLoading } = useLoginQuery();

  const submitHandler: (values: ILoginValues) => void = (
    values: ILoginValues
  ) => {
    loginUser(
      { ...values },
      {
        onSuccess: (resp: any) => {
          setAuthToken(resp?.token);
          onClose();
        },
        onError: (error) => {
          setFormErrors(error, setError);
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit(submitHandler)}>
      <ModalContent px={{ base: 0, md: 6 }} py={4}>
        <ModalHeader>
          <VStack align="flex-start">
            <Heading as="h2" fontSize={[20, 20, 30, 40]} color="teal.900">
              Welcome back!
            </Heading>
            <Text color="hjsBlack" fontWeight="500" fontSize={[12, 12, 18, 24]}>
              New user?{" "}
              <Button
                size="xl"
                fontWeight="500"
                color="hjsBlack"
                variant="link"
                onClick={() => changeModalType(ModalTypes.SIGNUP)}
                className="underline"
              >
                Sign up
              </Button>{" "}
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack mt={4} alignItems="flex-start" gap={4}>
            <TextField
              label="Email"
              placeholder="Enter your email"
              type="text"
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
              {...register("email")}
            />

            <TextField
              label="Password"
              placeholder="Enter your password"
              error={Boolean(errors.password)}
              helperText={errors.password?.message}
              {...register("password")}
              type="password"
            />

            <Button
              color="hjsBlack"
              fontWeight="400"
              fontSize={[12, 12, 18, 24]}
              variant="link"
              alignSelf="flex-end"
              m="initial !important"
              className="!underline"
              onClick={() => changeModalType(ModalTypes.FORGOT_PASSWORD)}
            >
              Forgot Password?
            </Button>
          </VStack>
        </ModalBody>
        <ModalFooter alignItems="center" justifyContent="center">
          <SecondaryButton
            my={4}
            // onClick={handleSubmit}
            type="submit"
            px={16}
            isLoading={isLoading}
            fontSize={[12, 12, 14, 16]}
          >
            Sign in
          </SecondaryButton>
        </ModalFooter>
      </ModalContent>
    </form>
  );
};

export default LoginForm;
